// PregnancyWelcomeWeb.jsx
// Web-mode welcome screen for pregnancy health screening
// Props: onNext, onBack (optional)

import React from "react";
import styles from "./PregRiskScreening.module.css";
import { useNavigate } from "react-router-dom";

/* ─── SVG Illustrations ─────────────────────────────────────────── */

const PregnantWomanSVG = () => (
  <svg width="320" height="380" viewBox="0 0 320 380" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Background soft blob */}
    <ellipse cx="160" cy="260" rx="130" ry="100" fill="#FDE8F3" opacity="0.5" />

    {/* Decorative leaves bottom-left */}
    <path d="M20 330 Q40 290 70 305 Q45 325 20 330Z" fill="#C4B5F0" opacity="0.45" />
    <path d="M10 310 Q35 275 60 295 Q35 312 10 310Z" fill="#D8CEF5" opacity="0.5" />
    <path d="M35 345 Q55 310 80 322 Q58 340 35 345Z" fill="#C4B5F0" opacity="0.4" />
    <line x1="42" y1="340" x2="55" y2="305" stroke="#B8A8E8" strokeWidth="1.5" />
    <line x1="22" y1="320" x2="38" y2="290" stroke="#B8A8E8" strokeWidth="1.5" />

    {/* Decorative leaves bottom-right */}
    <path d="M240 335 Q265 295 290 310 Q268 330 240 335Z" fill="#C4B5F0" opacity="0.45" />
    <path d="M258 350 Q278 315 300 328 Q278 345 258 350Z" fill="#D8CEF5" opacity="0.4" />
    <line x1="262" y1="345" x2="278" y2="312" stroke="#B8A8E8" strokeWidth="1.5" />

    {/* Flower */}
    <circle cx="55" cy="255" r="7" fill="#F9A8D4" opacity="0.65" />
    <circle cx="55" cy="244" r="5" fill="#FBCFE8" opacity="0.55" />
    <circle cx="64" cy="249" r="5" fill="#FBCFE8" opacity="0.55" />
    <circle cx="64" cy="261" r="5" fill="#FBCFE8" opacity="0.55" />
    <circle cx="46" cy="261" r="5" fill="#FBCFE8" opacity="0.55" />
    <circle cx="46" cy="249" r="5" fill="#FBCFE8" opacity="0.55" />
    <circle cx="55" cy="255" r="6" fill="#F472B6" opacity="0.75" />

    {/* Floating hearts */}
    <path d="M218 65 C218 62 215 59 212 62 C209 59 206 62 206 65 C206 69 212 73 212 73C212 73 218 69 218 65Z" fill="#F9A8D4" opacity="0.75" />
    <path d="M245 95 C245 93 243 91 241 93 C239 91 237 93 237 95 C237 97.5 241 100 241 100C241 100 245 97.5 245 95Z" fill="#FBCFE8" opacity="0.6" />
    <path d="M198 45 C198 43.5 196.5 42 195 43.5 C193.5 42 192 43.5 192 45 C192 47 195 49 195 49C195 49 198 47 198 45Z" fill="#F9A8D4" opacity="0.5" />

    {/* Body — dress */}
    <ellipse cx="165" cy="285" rx="52" ry="68" fill="#FDA4AF" />
    <ellipse cx="165" cy="265" rx="46" ry="55" fill="#FCA5A5" opacity="0.55" />

    {/* Belly bump */}
    <ellipse cx="170" cy="292" rx="44" ry="46" fill="#FCA5A5" />
    <ellipse cx="172" cy="295" rx="37" ry="40" fill="#FDB5B5" />

    {/* Dress top */}
    <ellipse cx="163" cy="228" rx="36" ry="38" fill="#FB7185" />
    <ellipse cx="163" cy="218" rx="28" ry="25" fill="#F9758A" />

    {/* Neck */}
    <rect x="157" y="194" width="16" height="20" rx="6" fill="#FDDCB5" />

    {/* Head */}
    <ellipse cx="165" cy="175" rx="30" ry="34" fill="#FDDCB5" />

    {/* Hair */}
    <path d="M135 170 Q138 135 165 138 Q192 135 196 158 Q200 172 195 182 Q192 164 188 168 Q184 145 165 146 Q146 145 142 170 Q138 178 135 170Z" fill="#2D1B4E" />
    <path d="M195 178 Q205 198 202 225 Q199 248 194 258 Q198 238 197 215 Q196 195 195 178Z" fill="#2D1B4E" />
    <path d="M135 170 Q130 188 133 210 Q136 230 140 240 Q136 220 135 200 Q134 184 135 170Z" fill="#2D1B4E" />
    <path d="M196 180 Q212 205 210 238 Q207 265 200 278 Q206 255 205 228 Q204 205 196 180Z" fill="#2D1B4E" />

    {/* Eyes */}
    <ellipse cx="157" cy="172" rx="4" ry="3.5" fill="#2D1B4E" />
    <ellipse cx="174" cy="172" rx="4" ry="3.5" fill="#2D1B4E" />
    <circle cx="158.5" cy="171" r="1.4" fill="white" />
    <circle cx="175.5" cy="171" r="1.4" fill="white" />
    {/* Blush */}
    <ellipse cx="150" cy="179" rx="6" ry="4" fill="#FCA5A5" opacity="0.5" />
    <ellipse cx="180" cy="179" rx="6" ry="4" fill="#FCA5A5" opacity="0.5" />
    {/* Smile */}
    <path d="M159 187 Q165 193 171 187" stroke="#E05A7A" strokeWidth="2" strokeLinecap="round" fill="none" />

    {/* Left arm */}
    <path d="M128 228 Q116 245 118 268 Q120 282 132 292" stroke="#FDDCB5" strokeWidth="16" strokeLinecap="round" fill="none" />
    {/* Right arm */}
    <path d="M198 232 Q210 250 208 272 Q206 284 196 292" stroke="#FDDCB5" strokeWidth="16" strokeLinecap="round" fill="none" />

    {/* Hands */}
    <ellipse cx="136" cy="296" rx="12" ry="8" fill="#FDDCB5" />
    <ellipse cx="194" cy="294" rx="12" ry="8" fill="#FDDCB5" />

    {/* Sparkles */}
    <circle cx="90" cy="160" r="3" fill="#F9A8D4" opacity="0.5" />
    <circle cx="248" cy="145" r="2.5" fill="#FBCFE8" opacity="0.6" />
    <circle cx="78" cy="200" r="2" fill="#C4B5F0" opacity="0.5" />
  </svg>
);

const ClockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9.5" stroke="var(--primary-color)" strokeWidth="1.8" />
    <path d="M12 6.5V12.5L15.5 14.5" stroke="var(--primary-color)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <rect x="4.5" y="10.5" width="15" height="10" rx="2.5" stroke="var(--primary-color)" strokeWidth="1.8" />
    <path d="M8 10.5V7.5C8 5.3 9.8 3.5 12 3.5C14.2 3.5 16 5.3 16 7.5V10.5" stroke="var(--primary-color)" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="12" cy="15.5" r="1.2" fill="var(--primary-color)" />
  </svg>
);

const InfoIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9.5" stroke="var(--primary-color)" strokeWidth="1.8" />
    <path d="M12 11V17" stroke="var(--primary-color)" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="12" cy="8" r="1" fill="var(--primary-color)" />
  </svg>
);

const ShieldIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M12 3L4 7V13C4 17.4 7.4 21.5 12 22.5C16.6 21.5 20 17.4 20 13V7L12 3Z" stroke="#7C3AED" strokeWidth="1.8" fill="var(--ava-bg-primary-light)" />
    <path d="M9 12L11 14L15 10" stroke="#7C3AED" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* ─── Main Component ────────────────────────────────────────────── */

// const PregnancyWelcomeWeb = ({ onNext,  }) => {
//   const navigate=useNavigate();
//   const features = [
//     { icon: <ClockIcon />, text: "Takes less than 10 minutes" },
//     { icon: <LockIcon />, text: "Private & confidential" },
//     { icon: <InfoIcon />, text: "Not a medical diagnosis" },
//   ];
// const onBack=()=>{
//   navigate('/')
// }
//   return (
//     <div className={styles.pageRoot}>
//       <header className={styles.topBar}>
//         {/* <div>
//           <img src="/Home/FemCareLogo.svg" style={{ height: "32px" }} />
//         </div> */}
//       </header>
//       {/* ── Progress bar ── */}
//       {/* <div className={styles.progressTrack}>
//         <div className={styles.progressFill} style={{ width: "9.09%" }} />
//       </div> */}
//       {/* ── Step badge ── */}
//       {/* <div className={styles.stepBadgeRow}>
//         <div className={styles.stepBadge}>
//           <span className={styles.stepNum}>1</span>
//           <span className={styles.stepLabel}>Screening Introduction</span>
//         </div>
//       </div> */}
//       {/* ── Main two-column layout ── */}
//       <main className={styles.mainGrid}>
//         {/* LEFT — text content */}
//         <section className={styles.leftCol}>
//           <div className={styles.eyebrow}>Pregnancy Health</div>
//           <h1 className={styles.headline}>
//             Let's assess your<br />
//             <span className={styles.headlineAccent}>pregnancy health</span>
//           </h1>
//           <p className={styles.bodyText}>
//             This screening helps us understand possible health risks during
//             pregnancy and ensure you receive the right care at the right time.
//           </p>

//           {/* Feature pills */}
//           <ul className={styles.featureList}>
//             {features.map(({ icon, text }, i) => (
//               <li key={i} className={styles.featureItem} style={{ animationDelay: `${0.15 + i * 0.1}s` }}>
//                 <span className={styles.featureIconWrap}>{icon}</span>
//                 <span className={styles.featureText}>{text}</span>
//               </li>
//             ))}
//           </ul>

//           {/* Privacy note */}
//           <div className={styles.privacyNote}>
//             <ShieldIcon />
//             <span>Your health data is safe with us and will never be shared.</span>
//           </div>

//           {/* CTAs */}
//           <div className={styles.ctaGroup}>
//             <button className={`btn-primary`} onClick={onNext}>
//               Start screening
//             </button>
//             <button className={`${styles.ghostBtn}`} onClick={onBack}>
//               I'll do it later
//             </button>
//           </div>
//         </section>

//         {/* RIGHT — illustration */}
//         <section className={styles.rightCol}>
//           <div className={styles.illustrationCard}>
//             <div className={styles.illustrationInner}>
//               <PregnantWomanSVG />
//             </div>
//             {/* Floating stat cards */}
//             <div className={styles.floatCardTop}>
//               <span className={styles.floatEmoji}>🌸</span>
//               <div>
//                 <div className={styles.floatTitle}>Quick & easy</div>
//                 <div className={styles.floatSub}>Under 10 minutes</div>
//               </div>
//             </div>
//             <div className={styles.floatCardBottom}>
//               <span className={styles.floatEmoji}>🔒</span>
//               <div>
//                 <div className={styles.floatTitle}>100% Private</div>
//                 <div className={styles.floatSub}>Encrypted & secure</div>
//               </div>
//             </div>
//           </div>
//         </section>

//       </main>
//     </div>
//   );
// };

const PregnancyWelcomeWeb = ({ eyebrow, headlineAccent, bodyText, illustration, onNext, onBack }) => {
  const navigate = useNavigate();
console.log({ eyebrow, headlineAccent, bodyText }); // add at top of component
  const features = [
    { icon: <ClockIcon />, text: "Takes less than 10 minutes" },
    { icon: <LockIcon />, text: "Private & confidential" },
    { icon: <InfoIcon />, text: "Not a medical diagnosis" },
  ];

  return (
    <div className={styles.pageRoot}>
      {/* <div style={{paddingLeft:"2rem",paddingTop:"2rem"}}>
        <img src="/Home/FemCareLogo.svg" style={{ height: "32px" }} />
      </div> */}
      <header className={styles.topBar} />
      <main className={styles.mainGrid}>
        <section className={styles.leftCol}>
          <div className={styles.eyebrow}>{eyebrow}</div>
          <h1 className={styles.headline}>
            Let's assess your<br />
            <span className={styles.headlineAccent}>{headlineAccent}</span>
          </h1>
          <p className={styles.bodyText}>{bodyText}</p>

          <ul className={styles.featureList}>
            {features.map(({ icon, text }, i) => (
              <li key={i} className={styles.featureItem} style={{ animationDelay: `${0.15 + i * 0.1}s` }}>
                <span className={styles.featureIconWrap}>{icon}</span>
                <span className={styles.featureText}>{text}</span>
              </li>
            ))}
          </ul>

          <div className={styles.privacyNote}>
            <ShieldIcon />
            <span>Your health data is safe with us and will never be shared.</span>
          </div>

          <div className={styles.ctaGroup}>
            <button className="btn-primary" onClick={onNext}>Start screening</button>
            <button className={styles.ghostBtn} onClick={onBack ?? (() => navigate('/'))}>I'll do it later</button>
          </div>
        </section>

        <section className={styles.rightCol}>
          {/* <section className={styles.rightCol}> */}
            <div className={styles.illustrationCard}>
              <div className={styles.illustrationInner}>
                {/* <PregnantWomanSVG /> */}
                <img src="/images/Landingprogram.svg" 
                
                // className="w-96 h-96"
                 />

              </div>
              {/* Floating stat cards */}
              <div className={styles.floatCardTop}>
                <span className={styles.floatEmoji}>🌸</span>
                <div>
                  <div className={styles.floatTitle}>Quick & easy</div>
                  <div className={styles.floatSub}>Under 10 minutes</div>
                </div>
              </div>
              <div className={styles.floatCardBottom}>
                <span className={styles.floatEmoji}>🔒</span>
                <div>
                  <div className={styles.floatTitle}>100% Private</div>               <div className={styles.floatSub}>Encrypted & secure</div>
                </div>
              </div>
            </div>
          {/* </section> */}
        </section>
      </main>
    </div>
  );
};
export default PregnancyWelcomeWeb;